import { useCallback, useMemo } from "react";
import { useLeadForm } from "@/features/leads/contexts/FormLeadContext";
import { useLeadFormLockedFields } from "@/features/leads/api/useLeadFormConfig";
import { useLeadMutations } from "@/features/leads/api/useLeadMutations";
import { useChildMutations } from "@/features/leads/api/useChildMutations";
import { useActiveDataMutation } from "@/features/leads/hook/useActiveDataMutation";
import { usePermissions } from "@/rbac/usePermissions";
import { dealStageIdOf, useCanEditDeal } from "@/rbac/useCanEditDeal";
import { normalizeCustomInputScopesForForm } from "@/utils/getFromCustom";

export const FIELD_LOCK_MESSAGE =
  "Locked by your administrator — this field can't be changed.";

type CustomFieldPatch = { optionIds?: string[]; textValue?: string };

/**
 * Replace (or add) ONE custom field's entry while carrying every other
 * scope through untouched.
 *
 * This matters: the server's `customModelSync` DELETES every scope row for
 * the context and recreates it from the payload, so a partial array would
 * silently wipe the lead's other custom-field values. The base always comes
 * from the API record (`lead.customInputScopes` / `child.customInputScopes`)
 * rather than the RHF form — the form's copy gets re-aligned to the visible
 * field list when the global edit panel mounts, which can drop entries for
 * fields outside the current stage/campaign.
 */
function mergeCustomScope(
  base: any[],
  customInputId: number,
  patch: CustomFieldPatch,
): any[] {
  const id = Number(customInputId);
  let found = false;
  const merged = (base ?? []).map((scope: any) => {
    if (Number(scope?.customInputId) !== id) return scope;
    found = true;
    return { ...scope, ...patch };
  });
  if (!found) merged.push({ customInputId: id, ...patch });
  return merged;
}

/**
 * Everything the Overview cards need to edit a single field in place:
 * permission + lock gating, and one-field-at-a-time save calls that PATCH
 * only what changed (never the whole edit form).
 */
export function useInlineFieldEdit() {
  const { canGlobal } = usePermissions();
  const canEditDeal = useCanEditDeal();
  const { isViewLocked } = useLeadForm();
  const { isFieldLocked } = useLeadFormLockedFields();
  const { lead, child } = useActiveDataMutation();
  const { updateLead } = useLeadMutations();
  const { updateChild } = useChildMutations();

  /**
   * Same grant that gates the drawer's global edit pencil
   * (`Can moduleKey="lead_management" action="edit"`), so inline editing is
   * never a way around it. Revoked leads stay read-only.
   */
  const canInlineEdit = canGlobal("lead_management", "edit") && !isViewLocked;
  /**
   * Deal rows (the deal's own CFs) additionally need "Edit choices" on the
   * DEAL's stage — see `useCanEditDeal`. Lead rows keep `canInlineEdit`.
   */
  const canInlineEditDeal = canInlineEdit && canEditDeal(dealStageIdOf(child));

  const lockReason = useCallback(
    (lockKey?: string) =>
      lockKey && isFieldLocked(lockKey) ? FIELD_LOCK_MESSAGE : undefined,
    [isFieldLocked],
  );

  const saveLeadFields = useCallback(
    async (data: Record<string, unknown>) => {
      if (!lead?.id) return;
      await updateLead.mutateAsync({ id: lead.id, data });
    },
    [lead, updateLead],
  );

  const saveLeadCustomField = useCallback(
    async (customInputId: number, patch: CustomFieldPatch) => {
      if (!lead?.id) return;
      const base = normalizeCustomInputScopesForForm(lead?.customInputScopes);
      await updateLead.mutateAsync({
        id: lead.id,
        data: {
          customInputScopes: mergeCustomScope(base, customInputId, patch),
        },
      });
    },
    [lead, updateLead],
  );

  const saveChildCustomField = useCallback(
    async (customInputId: number, patch: CustomFieldPatch) => {
      if (!child?.id) return;
      const base = normalizeCustomInputScopesForForm(child?.customInputScopes);
      await updateChild.mutateAsync({
        id: child.id,
        data: {
          customInputScopes: mergeCustomScope(base, customInputId, patch),
        },
      });
    },
    [child, updateChild],
  );

  return useMemo(
    () => ({
      canInlineEdit,
      canInlineEditDeal,
      lockReason,
      saveLeadFields,
      saveLeadCustomField,
      saveChildCustomField,
    }),
    [
      canInlineEdit,
      canInlineEditDeal,
      lockReason,
      saveLeadFields,
      saveLeadCustomField,
      saveChildCustomField,
    ],
  );
}
